import React, { useState } from 'react';
import { 
  ArrowLeft, Edit2, Package, Tag, Briefcase, History, Settings, 
  Trash2, Plus, Calendar, Clock, DollarSign, Layers, List, 
  Info, AlertCircle, CheckCircle2, ChevronRight
} from 'lucide-react';
import { cn } from '../lib/utils';

interface ItemProfileViewProps {
  item: any;
  currency: string;
  onBack: () => void;
  onEdit: () => void;
  language?: string;
}

export default function ItemProfileView({ item, currency, onBack, onEdit, language = 'en' }: ItemProfileViewProps) {
  const [activeTab, setActiveTab] = useState<'overview' | 'history' | 'recipe' | 'variants'>('overview');

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-700 dark:bg-green-700/20 dark:text-green-400';
      case 'archived': return 'bg-red-100 text-red-700 dark:bg-red-700/20 dark:text-red-400';
      default: return 'bg-gray-100 text-gray-700 dark:bg-gray-700/20 dark:text-gray-400';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <button 
            onClick={onBack} 
            className="p-2 border border-[#141414] rounded-full hover:bg-[#141414] hover:text-[#E4E3E0] transition-colors dark:border-white dark:hover:bg-white dark:hover:text-black"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-4">
            {item.imageUrl ? (
              <div className="w-16 h-16 rounded-2xl border border-[#141414]/10 overflow-hidden bg-white">
                <img src={item.imageUrl} alt={item.name} className="w-full h-full object-cover" />
              </div>
            ) : (
              <div className="w-16 h-16 rounded-2xl border border-[#141414]/10 bg-white dark:bg-white/5 flex items-center justify-center">
                <Package className="w-8 h-8 opacity-20" />
              </div>
            )}
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold tracking-tight">{item.name}</h1>
                <span className={cn("px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider", getStatusColor(item.status))}>
                  {item.status}
                </span>
              </div>
              <p className="text-sm opacity-50 italic serif flex items-center gap-2">
                <span className="font-mono text-[10px] bg-[#141414]/5 px-2 py-0.5 rounded-lg">SKU: {item.code}</span>
              </p>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <button 
            onClick={onEdit} 
            className="bg-white text-[#141414] border border-[#141414] px-4 py-2 rounded-full font-bold flex items-center gap-2 hover:bg-[#141414] hover:text-white transition-all text-sm dark:bg-black dark:text-white dark:border-white dark:hover:bg-white dark:hover:text-black"
          >
            <Edit2 className="w-4 h-4" /> 
            {language === 'ar' ? 'تعديل الصنف' : 'Edit Item'}
          </button>
        </div>
      </div>

      {/* Quick Stats / Flash Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <FlashCard 
          label="Unit Price" 
          value={`${currency} ${(item.unitPrice || 0).toLocaleString()}`} 
          icon={<DollarSign className="w-4 h-4" />}
          color="blue"
        />
        <FlashCard 
          label="Cost Price" 
          value={`${currency} ${item.costPrice?.toLocaleString() || 0}`} 
          icon={<BudgetIcon />}
          color="orange"
        />
        <FlashCard 
          label="Margin" 
          value={`${Math.round(((item.unitPrice - (item.costPrice || 0)) / item.unitPrice) * 100)}%`} 
          icon={<TrendIcon />}
          color="green"
        />
        <FlashCard 
          label="On Hand" 
          value={item.quantity?.toLocaleString() || 0} 
          icon={<Layers className="w-4 h-4" />}
          color="purple"
        />
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 border-b border-[#141414]/10 dark:border-white/10 pb-2 overflow-x-auto custom-scrollbar">
        {[
          { id: 'overview', label: 'Overview', icon: Info },
          { id: 'history', label: 'History & Logs', icon: History },
          { id: 'recipe', label: 'Recipe', icon: List, hidden: !item.recipes || item.recipes.length === 0 },
          { id: 'variants', label: 'Variants', icon: Layers, hidden: !item.hasVariants }
        ].filter(tab => !tab.hidden).map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={cn(
              "flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold transition-all whitespace-nowrap",
              activeTab === tab.id 
                ? "bg-[#141414] text-[#E4E3E0] dark:bg-white dark:text-black" 
                : "hover:bg-[#141414]/5 dark:hover:bg-white/5 opacity-60 hover:opacity-100"
            )}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      <div className="bg-white/50 dark:bg-white/5 border border-[#141414] dark:border-white/10 rounded-[2rem] p-6 min-h-[400px]">
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="space-y-8">
              <section>
                <h3 className="text-sm font-bold uppercase tracking-widest opacity-40 border-b border-[#141414]/10 dark:border-white/10 pb-2 mb-4">Item Details</h3>
                <div className="space-y-4">
                  <DetailItem label="Type" value={item.type.toUpperCase()} icon={<Tag className="w-4 h-4" />} />
                  <DetailItem label="SKU (Stock Keeping Unit)" value={item.code} icon={<Info className="w-4 h-4" />} />
                  <DetailItem label="Barcode" value={item.barcode || 'None'} icon={<AlertCircle className="w-4 h-4" />} />
                </div>
              </section>

              <section>
                <h3 className="text-sm font-bold uppercase tracking-widest opacity-40 border-b border-[#141414]/10 dark:border-white/10 pb-2 mb-4">Stock Settings</h3>
                <div className="space-y-4">
                  <StatusCheck label="Track Inventory" checked={item.trackInventory} />
                  <StatusCheck label="POS Enabled" checked={item.enablePOS} />
                  <StatusCheck label="Allow Negative Stock" checked={item.allowNegativeStock} />
                  <StatusCheck label="Has Expiry Date" checked={item.hasExpiry} />
                  <StatusCheck label="Hide from POS" checked={item.hideFromSellScreen} />
                </div>
              </section>

              {item.type === 'product' && (
                <section>
                  <h3 className="text-sm font-bold uppercase tracking-widest opacity-40 border-b border-[#141414]/10 dark:border-white/10 pb-2 mb-4">
                    {language === 'ar' ? 'المخزون حسب المستودع واللوط' : 'Warehouse Stock & Lot Details'}
                  </h3>
                  <div className="space-y-3">
                    {item.stockRecords && item.stockRecords.length > 0 ? (
                      item.stockRecords.map((rec: any, idx: number) => (
                        <div key={idx} className="flex flex-col gap-2 p-4 bg-[#141414]/5 dark:bg-white/5 rounded-2xl border border-[#141414]/5">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <Package className="w-4 h-4 opacity-30" />
                              <span className="text-sm font-bold">{rec.warehouseName}</span>
                            </div>
                            <span className="bg-white dark:bg-black px-3 py-1 rounded-lg border border-[#141414]/10 dark:border-white/10 text-sm font-mono font-bold">
                              {rec.quantity.toLocaleString()}
                            </span>
                          </div>
                          {(rec.lotNumber || rec.expiryDate) && (
                            <div className="flex items-center gap-3 mt-1 pt-2 border-t border-[#141414]/5">
                              {rec.lotNumber && (
                                <div className="flex items-center gap-1 bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded text-[10px] font-bold">
                                  <Tag className="w-3 h-3" />
                                  LOT: {rec.lotNumber}
                                </div>
                              )}
                              {rec.expiryDate && (
                                <div className={cn(
                                  "flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold",
                                  new Date(rec.expiryDate) < new Date() ? "bg-red-100 text-red-600" : "bg-blue-100 text-blue-600"
                                )}>
                                  <Clock className="w-3 h-3" />
                                  EXP: {rec.expiryDate}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))
                    ) : item.storeAvailability && item.storeAvailability.some((sa: any) => sa.enabled) ? (
                      item.storeAvailability.filter((sa: any) => sa.enabled).map((sa: any, idx: number) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-[#141414]/5 dark:bg-white/5 rounded-xl border border-[#141414]/5">
                          <div className="flex items-center gap-3">
                            <div className="w-2 h-2 rounded-full bg-green-500"></div>
                            <span className="text-sm font-bold">{sa.storeName}</span>
                          </div>
                          <span className="bg-white dark:bg-black px-3 py-1 rounded-lg border border-[#141414]/10 dark:border-white/10 text-sm font-mono font-bold">
                            {sa.quantity.toLocaleString()}
                          </span>
                        </div>
                      ))
                    ) : (
                      <div className="p-8 text-center border-2 border-dashed border-[#141414]/10 dark:border-white/10 rounded-2xl">
                        <Package className="w-8 h-8 opacity-20 mx-auto mb-2" />
                        <p className="text-xs opacity-50 font-bold uppercase tracking-widest">No stock records found</p>
                      </div>
                    )}
                  </div>
                </section>
              )}
            </div>

            <div className="space-y-8">
              <section>
                <h3 className="text-sm font-bold uppercase tracking-widest opacity-40 border-b border-[#141414]/10 dark:border-white/10 pb-2 mb-4">Description</h3>
                <p className="text-sm opacity-80 whitespace-pre-wrap italic serif leading-relaxed">
                  {item.description || 'No description provided.'}
                </p>
              </section>

              {item.tags && item.tags.length > 0 && (
                <section>
                  <h3 className="text-sm font-bold uppercase tracking-widest opacity-40 border-b border-[#141414]/10 dark:border-white/10 pb-2 mb-4">Tags</h3>
                  <div className="flex flex-wrap gap-2">
                    {item.tags.map((tag: string, i: number) => (
                      <span key={i} className="px-3 py-1 bg-[#141414]/5 dark:bg-white/5 border border-[#141414]/10 rounded-full text-[10px] font-bold uppercase tracking-wider">
                        {tag}
                      </span>
                    ))}
                  </div>
                </section>
              )}
            </div>
          </div>
        )}

        {activeTab === 'history' && (
          <div className="space-y-6">
            <h3 className="text-lg font-bold italic serif tracking-tight">Audit Trail</h3>
            <div className="space-y-4">
              {item.actionLog?.slice().reverse().map((log: any, idx: number) => (
                <div key={idx} className="flex gap-4 p-4 rounded-2xl border border-[#141414]/10 dark:border-white/10 bg-[#141414]/5 dark:bg-white/5">
                  <div className="p-2 h-fit bg-white dark:bg-black rounded-lg border border-[#141414]/10 dark:border-white/10">
                    <History className="w-4 h-4 opacity-50" />
                  </div>
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <p className="font-bold text-sm">{log.action}</p>
                      <span className="text-[10px] opacity-40 uppercase tracking-widest font-bold">
                        {log.at?.toDate ? new Date(log.at.toDate()).toLocaleString() : new Date(log.at).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-xs opacity-60">By {log.by} ({log.byEmail})</p>
                    {log.details && (
                      <p className="text-xs mt-2 p-2 bg-black/5 dark:bg-white/5 rounded-lg font-mono">{log.details}</p>
                    )}
                  </div>
                </div>
              ))}
              {(!item.actionLog || item.actionLog.length === 0) && (
                <div className="text-center py-12 opacity-50 italic serif">No history records found.</div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'recipe' && item.recipes && (
          <div className="space-y-6">
             <h3 className="text-lg font-bold italic serif tracking-tight">Product Formulations</h3>
             <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
               {item.recipes.map((recipe: any) => (
                 <div key={recipe.id} className={cn(
                   "p-6 rounded-3xl border transition-all",
                   recipe.isActive 
                    ? "bg-[#141414] text-[#E4E3E0] border-[#141414]" 
                    : "bg-[#141414]/5 dark:bg-white/5 border-[#141414]/10 dark:border-white/10"
                 )}>
                   <div className="flex items-center justify-between mb-6">
                     <div>
                       <h4 className="font-bold text-lg italic serif">{recipe.name || 'Untitled Recipe'}</h4>
                       <p className="text-[10px] uppercase tracking-widest font-bold opacity-60">
                         {recipe.isActive ? 'Current Formulation' : 'Alternative'}
                       </p>
                     </div>
                     <div className={cn("p-2 rounded-full", recipe.isActive ? "bg-white/20" : "bg-black/5")}>
                       <CheckCircle2 className="w-5 h-5" />
                     </div>
                   </div>
                   
                   <div className="space-y-3">
                     {recipe.components.map((comp: any, i: number) => (
                       <div key={i} className="flex items-center justify-between py-2 border-b border-white/10 last:border-0">
                         <div className="flex items-center gap-3">
                           <div className="w-1.5 h-1.5 rounded-full bg-current opacity-30" />
                           <span className="text-sm font-medium">{comp.name}</span>
                         </div>
                         <div className="flex items-center gap-2 font-mono text-sm">
                           <span className="font-bold">{comp.quantity}</span>
                           <span className="opacity-60">{comp.unit}</span>
                         </div>
                       </div>
                     ))}
                   </div>
                 </div>
               ))}
             </div>
          </div>
        )}

        {activeTab === 'variants' && item.variants && (
          <div className="space-y-6">
             <h3 className="text-lg font-bold italic serif tracking-tight">Product Variations</h3>
             <div className="bg-white/50 dark:bg-white/5 border border-[#141414]/10 dark:border-white/10 rounded-2xl overflow-hidden">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-[#141414]/10">
                      <th className="p-4 text-[10px] font-bold uppercase tracking-widest opacity-40 italic serif">Variant Name</th>
                      <th className="p-4 text-[10px] font-bold uppercase tracking-widest opacity-40 italic serif">SKU</th>
                      <th className="p-4 text-[10px] font-bold uppercase tracking-widest opacity-40 italic serif">Cost</th>
                      <th className="p-4 text-[10px] font-bold uppercase tracking-widest opacity-40 italic serif">Price</th>
                      <th className="p-4 text-[10px] font-bold uppercase tracking-widest opacity-40 italic serif">Stock</th>
                    </tr>
                  </thead>
                  <tbody>
                    {item.variants.map((v: any) => (
                      <tr key={v.id} className="border-b border-[#141414]/5 hover:bg-[#141414]/5 dark:hover:bg-white/5 transition-colors">
                        <td className="p-4 text-sm font-bold">{v.name}</td>
                        <td className="p-4 font-mono text-xs opacity-60">{v.sku}</td>
                        <td className="p-4 font-mono text-xs">{currency} {v.costPrice?.toLocaleString()}</td>
                        <td className="p-4 font-mono text-xs font-bold">{currency} {v.unitPrice?.toLocaleString()}</td>
                        <td className="p-4">
                          <span className={cn(
                            "px-2 py-0.5 rounded-full text-[10px] font-bold font-mono",
                            v.quantity > 0 ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                          )}>
                            {v.quantity || 0}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
             </div>
          </div>
        )}
      </div>
    </div>
  );
}

function FlashCard({ label, value, icon, color }: { label: string, value: string, icon: any, color: string }) {
  const colors: Record<string, string> = {
    blue: 'border-blue-500/20 bg-blue-500/5 text-blue-600 dark:text-blue-400',
    orange: 'border-orange-500/20 bg-orange-500/5 text-orange-600 dark:text-orange-400',
    green: 'border-green-500/20 bg-green-500/5 text-green-600 dark:text-green-400',
    purple: 'border-purple-500/20 bg-purple-500/5 text-purple-600 dark:text-purple-400',
  };

  return (
    <div className={cn("p-4 rounded-3xl border border-[#141414] dark:border-white/10 flex flex-col justify-between h-28 bg-white dark:bg-white/5 shadow-sm group hover:scale-[1.02] transition-all cursor-default", colors[color])}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-bold uppercase tracking-widest opacity-60">{label}</span>
        <div className="opacity-40 group-hover:opacity-100 transition-opacity">
          {icon}
        </div>
      </div>
      <p className="text-2xl font-bold tracking-tight italic serif -mb-1">{value}</p>
    </div>
  );
}

function DetailItem({ label, value, icon }: { label: string, value: string, icon: any }) {
  return (
    <div className="flex items-center justify-between group">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-black/5 dark:bg-white/5 opacity-40 group-hover:opacity-100 transition-opacity">
          {icon}
        </div>
        <span className="text-xs font-bold uppercase tracking-widest opacity-50">{label}</span>
      </div>
      <span className="text-sm font-bold">{value}</span>
    </div>
  );
}

function StatusCheck({ label, checked }: { label: string, checked: boolean }) {
  return (
    <div className="flex items-center justify-between group">
      <span className="text-sm font-medium opacity-70 group-hover:opacity-100 transition-opacity">{label}</span>
      <div className={cn(
        "p-1 rounded-full",
        checked ? "text-green-500 bg-green-500/10" : "text-red-500 bg-red-500/10"
      )}>
        {checked ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
      </div>
    </div>
  );
}

function BudgetIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
  );
}

function TrendIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>
  );
}
